import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/// One way the supplier would deliver the size on screen, as the storefront may know it: no
/// service codes, no origin - and carrier names without the supplier's (SupplierMask, server side).
export interface DeliveryOption
{
    Company: string;
    Fee: number;
    Currency: string;
    FreeShipping: boolean;
    MinDeliveryDays: number;
    MaxDeliveryDays: number;
    Tracking: boolean;
}

/// One country of a product's delivery table: whether it delivers there, how fast, and what
/// delivery costs a shopper there (Fee, below).
export interface CountryShipping
{
    Code: string;
    Name: string;
    /// 'ships' or 'no'. For a country nobody has asked about yet the answer is empty.
    State: string;
    MinDays: number;
    MaxDays: number;
    Carrier: string;
    Tracked: boolean;
    ShipFrom: string;
    /// The parcel stays inside the shopper's own customs area (EU single market, or the country
    /// itself). The product page's "Local warehouse" pill and "No customs or import fees" (shipsLocal).
    Local?: boolean;
    /// What delivery of one unit costs a shopper there, in USD; 0 is free. Null where the country
    /// has no rate of its own - the map's DefaultFee stands in.
    Fee?: number;
    /// What the unit price carries there in place of a small delivery fee (the owner's rule); 0 otherwise.
    Surcharge?: number;
    CheckedAt: string;
    Warnings: string[];
}

/// One size as one country is offered it: its price and stock there, and - where the owner's rules
/// for delivery fees were worked out with the product's prices - what delivery of one unit of it is
/// charged (0 is free). Without Delivery the product's own rate for the country stands (CountryShipping.Fee).
export interface ProductOfferRow
{
    OptionId: number;
    Price: number;
    RegularPrice: number;
    Discount: number;
    Stock: number;
    Delivery?: number;
}

export interface CartShippingLine { ProductId: number; OptionId?: number; Quantity: number; PerUnit: number; Amount: number; }
export interface CartShippingQuote { Country: string; Total: number; Lines: CartShippingLine[]; Failed?: boolean; }

export interface ProductShippingMap
{
    ProductId: number;
    ShipsToCount: number;
    /// Delivery is checked country by country for this product: a country missing from Countries
    /// has not been checked yet. False for a product added by hand.
    CheckedPerCountry?: boolean;
    /// The delivery rate for every country with none of its own: the default country's (US), else 0.
    DefaultFee?: number;
    DefaultFeeCountry?: string;
    /// The price surcharge that goes with DefaultFee, where the default country's fee is a small one.
    DefaultSurcharge?: number;
    /// The shop's own handling time, in days, in front of every country's window: a promised
    /// delivery is today + this + MinDays/MaxDays, on the page and in the structured data alike.
    HandlingMinDays?: number;
    HandlingMaxDays?: number;
    /// A seller's product: the rows are the seller's own delivery terms, the handling days the
    /// seller's, and a country with no row is one the seller does not deliver to.
    SellerDelivery?: boolean;
    CheckedAt: string;
    Countries: CountryShipping[];
}

/// Where a product sells and how it gets there. productMap is public: it feeds the product's
/// structured data (per-country delivery windows, for search engines) and the visitor's own
/// line in the shipping box. It is never rendered as a list - a shopper sees their own country
/// only. Everything else is the owner's and answers 403 otherwise.
@Injectable({ providedIn: 'root' })
export class EligibilityService
{
    private readonly baseUrl = environment.apiUrl + '/api/Eligibility';

    constructor(private http: HttpClient) { }

    productMap(productId: number): Observable<ProductShippingMap>
    {
        return this.http.get<ProductShippingMap>(`${this.baseUrl}/ProductMap/${productId}`);
    }

    /// For a product held in several warehouses: each size's price and stock from the warehouse
    /// that delivers to one country. Empty for everything else.
    /// What a basket's delivery comes to: each product's rate for the country times its quantity.
    /// The country is the delivery address's once one is chosen, else the one named, else the
    /// visitor's as the site places them. The checkout charges the same figure, worked out the
    /// same way on the server.
    cartShipping(items: { ProductId: number, Quantity: number }[], addressId: number = 0, country: string = ''): Observable<CartShippingQuote>
    {
        return this.http.post<CartShippingQuote>(`${this.baseUrl}/CartShipping`, { AddressId: addressId || 0, Country: country || null, Items: items });
    }

    productOffer(productId: number, countryCode: string): Observable<ProductOfferRow[]>
    {
        return this.http.get<any[]>(`${this.baseUrl}/ProductOffer/${productId}/${encodeURIComponent(countryCode)}`);
    }

    /// What the "not available in your country" page shows: the product, the country, and what
    /// can be bought there instead. Blocked is false when it does sell there after all.
    unavailable(slug: string, country: string): Observable<any>
    {
        const query = country ? `?country=${encodeURIComponent(country)}` : '';
        return this.http.get<any>(`${this.baseUrl}/Unavailable/${encodeURIComponent(slug)}${query}`);
    }

    /// A live delivery quote for the size on screen, asked by the shop's own ids: the server looks
    /// up the listing and the SKU of the warehouse serving the country, so neither is ever sent to
    /// a browser. Answers Ok: false for a product with nothing to quote.
    deliveryQuote(productId: number, optionId: number, country: string): Observable<{ Ok: boolean, Country: string, Options: DeliveryOption[] }>
    {
        const params = { productId: String(productId || 0), optionId: String(optionId || 0), country: country || '' };
        return this.http.get<any>(environment.apiUrl + '/api/Delivery/Quote', { params });
    }

    shipToCountries(): Observable<{ Code: string, Name: string }[]>
    {
        return this.http.get<{ Code: string, Name: string }[]>(`${this.baseUrl}/ShipToCountries`);
    }

    countries(): Observable<any>
    {
        return this.http.get<any>(`${this.baseUrl}/Countries`);
    }

    saveCountry(model: any): Observable<any>
    {
        return this.http.post<any>(`${this.baseUrl}/SaveCountry`, model);
    }

    matrix(productId: number): Observable<any>
    {
        return this.http.get<any>(`${this.baseUrl}/Matrix/${productId}`);
    }

    addOverride(model: { ProductId: number, CountryCode: string, Decision: number, Reason: string, ExpiresAt?: string }): Observable<any>
    {
        return this.http.post<any>(`${this.baseUrl}/AddOverride`, model);
    }

    retireOverride(id: number): Observable<any>
    {
        return this.http.post<any>(`${this.baseUrl}/RetireOverride/${id}`, {});
    }

    /// One country due now for every followed product; the worker's sweep asks them over the next minutes.
    checkCountry(code: string): Observable<any>
    {
        return this.http.post<any>(`${this.baseUrl}/CheckCountry/${encodeURIComponent(code)}`, {});
    }

    recheck(productId: number): Observable<any>
    {
        return this.http.post<any>(`${this.baseUrl}/Recheck/${productId}`, {});
    }
}
